import { useEffect, useMemo, useState } from "react"
import { ACTIONS, EVENTS, STATUS, type CallBackProps, type Step } from "react-joyride"
import { DateTime } from "luxon"

import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useAppSelector } from "../../../../../store/storeHooks"
import { LocalCache, StorageKey } from "../../../../../../shared/cache/LocalCache"
import { StandardTour } from "../../../../../../shared/components/tour/StandardTour"
import { ProductTourTooltip } from "../../../../../../shared/components/tour/ProductTourTooltip"
import { waitForTourTarget, closeOpenOverlay, clickTourTarget } from "../../../../../../shared/components/tour/tourUtils"

type TourUserCompletion = Readonly<{
    completed: true
    completedAt: string
}>

type TourCompletionByUserId = Record<string, TourUserCompletion | undefined>

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object"
}

function openEnvironmentMenu() {
    clickTourTarget("[data-tour='merchant-product-tour-environment']")
}

function openNewMenu() {
    clickTourTarget("[data-tour='merchant-product-tour-new']")
}

function openOrderNotificationsPopover() {
    clickTourTarget("[data-tour='merchant-product-tour-order-notifications']")
}

function clickEnvironmentReports() {
    openEnvironmentMenu()
    window.setTimeout(() => {
        clickTourTarget("[data-tour='merchant-product-tour-environment-reports']")
    }, 150)
}

function parseCompletionMap(raw: string | null): TourCompletionByUserId {
    if (!raw) return {}

    try {
        const parsed: unknown = JSON.parse(raw)
        if (!isRecord(parsed)) return {}
        const result: TourCompletionByUserId = {}

        for (const [key, value] of Object.entries(parsed)) {
            if (!/^[0-9]+$/.test(key)) continue
            if (!isRecord(value)) continue
            if (value["completed"] !== true) continue
            if (typeof value["completedAt"] !== "string" || !value["completedAt"]) continue

            result[key] = { completed: true, completedAt: value["completedAt"] }
        }

        return result
    } catch {
        return {}
    }
}

function upsertCompleted(
    previous: TourCompletionByUserId,
    userId: number,
    completedAtIso: string,
): TourCompletionByUserId {
    return {
        ...previous,
        [String(userId)]: { completed: true, completedAt: completedAtIso },
    }
}

export function MerchantProductTour() {
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectEffectiveUserId)
    const localCache = LocalCache()

    const [run, setRun] = useState(false)
    const [stepIndex, setStepIndex] = useState(0)
    const [completionMap, setCompletionMap] = useState<TourCompletionByUserId>(() =>
        parseCompletionMap(localCache.get(StorageKey.MERCHANT_PRODUCT_TOUR_V1)),
    )

    const isCompletedForUser = userId ? completionMap[String(userId)]?.completed === true : true
    const [forceRunOnMount, setForceRunOnMount] = useState(false)

    useEffect(() => {
        const raw = localCache.get(StorageKey.MERCHANT_PRODUCT_TOUR_FORCE_RUN)
        if (raw === "1") {
            setForceRunOnMount(true)
            localCache.set(StorageKey.MERCHANT_PRODUCT_TOUR_FORCE_RUN, "0")
        }
    }, [localCache])

    useEffect(() => {
        if (!forceRunOnMount) return

        setStepIndex(0)
        setRun(true)
        setForceRunOnMount(false)
    }, [forceRunOnMount])

    useEffect(() => {
        if (!userId) return
        if (isCompletedForUser) return

        setStepIndex(0)
        setRun(true)
    }, [isCompletedForUser, userId])

    useEffect(() => {
        if (!userId) return

        const handleStartTour = () => {
            setStepIndex(0)
            setRun(true)
        }

        window.addEventListener("merchant-product-tour:start", handleStartTour)

        return () => {
            window.removeEventListener("merchant-product-tour:start", handleStartTour)
        }
    }, [userId])

    const steps = useMemo<Step[]>(
        () => [
            {
                target: "body",
                placement: "center",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-welcome">
                        <>Welcome to Facilitynet. This is a quick introduction on how to use it.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-order-notifications']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-order-notifications-button">
                        <>Click here to open an overview of todays orders and activities.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-order-notifications-content']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-order-notifications">
                        <>Here you can get an overview of todays orders and activities, that need your attention.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-new']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-new-button">
                        <>Click here to open shortcuts for creating messages and orders.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-new-content']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-new">
                        <>
                            Click the New button to create push messages, create meeting service order on behalf of
                            customers and easy shortcuts access
                        </>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-environment']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-environment-button">
                        <>Click here to open the menu for switching between environments.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-environment-content']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-environment">
                        <>
                            The portal is split into 3 environments that each focus on a particular subject. You change
                            environment here.
                        </>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-nav-production']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-nav-production">
                        <>In Production you can plan and manage orders.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-nav-menu-planning']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-nav-menu-planning">
                        <>In Menu Planning, you can create weekly menus to keep your customers informed.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-nav-purchase-control']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-nav-purchase-control">
                        <>Use Purchase control to check that everyone pays for their food.</>
                    </LocalizedStrict>
                ),
            },
            {
                target: "[data-tour='merchant-product-tour-nav-survey']",
                placement: "auto",
                disableBeacon: true,
                content: (
                    <LocalizedStrict id="merchant-product-tour-step-nav-survey">
                        <>View the results of user feedback and keep track of user satisfaction.</>
                    </LocalizedStrict>
                ),
            },
        ],
        [],
    )

    const locale = useMemo(
        () => ({
            back: l10n.getString("merchant-product-tour-locale-back"),
            close: l10n.getString("merchant-product-tour-locale-close"),
            last: l10n.getString("merchant-product-tour-locale-last"),
            next: l10n.getString("merchant-product-tour-locale-next"),
            skip: l10n.getString("merchant-product-tour-locale-skip"),
        }),
        [l10n],
    )

    function goToStep(nextIndex: number) {
        // Ensure we don't stack multiple open menus/popovers.
        closeOpenOverlay()
        closeOpenOverlay()

        if (nextIndex === 2) {
            openOrderNotificationsPopover()
            void waitForTourTarget("[data-tour='merchant-product-tour-order-notifications-content']").then((found) => {
                if (!found) return
                setStepIndex(2)
            })
            return
        }

        if (nextIndex === 4) {
            openNewMenu()
            void waitForTourTarget("[data-tour='merchant-product-tour-new-content']").then((found) => {
                if (!found) return
                setStepIndex(4)
            })
            return
        }

        if (nextIndex === 6) {
            openEnvironmentMenu()
            void waitForTourTarget("[data-tour='merchant-product-tour-environment-content']").then((found) => {
                if (!found) return
                setStepIndex(6)
            })
            return
        }

        if (nextIndex === 7) {
            clickEnvironmentReports()
            void waitForTourTarget("[data-tour='merchant-product-tour-nav-list']", {
                attempts: 30,
                intervalMs: 50,
            }).then((found) => {
                setStepIndex(7)
            })
            return
        }

        setStepIndex(nextIndex)
    }

    function persistCompleted(currentUserId: number) {
        const completedAt = DateTime.utc().toISO()
        if (!completedAt) return
        setCompletionMap((previous) => {
            const updated = upsertCompleted(previous, currentUserId, completedAt)
            localCache.set(StorageKey.MERCHANT_PRODUCT_TOUR_V1, JSON.stringify(updated))
            return updated
        })
    }

    function handleCallback(data: CallBackProps) {
        const { action, index, status, type } = data

        if (!userId) return

        const isFinished = status === STATUS.FINISHED
        const isSkipped = status === STATUS.SKIPPED
        const isClosed = action === ACTIONS.CLOSE
        const isSkippedAction = action === ACTIONS.SKIP

        if (isFinished || isSkipped || isClosed || isSkippedAction) {
            persistCompleted(userId)
            closeOpenOverlay()
            setRun(false)
            return
        }

        const isStepAfter = type === EVENTS.STEP_AFTER
        const isTargetNotFound = type === EVENTS.TARGET_NOT_FOUND
        if (isStepAfter || isTargetNotFound) {
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)
            goToStep(nextIndex)
        }
    }

    if (!userId) return null

    return (
        <StandardTour
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            callback={handleCallback}
            locale={locale}
            tooltipComponent={ProductTourTooltip}
        />
    )
}
